import * as React from 'react'

import { CheckIcon, GlobeIcon, GripVerticalIcon, ImageIcon } from 'lucide-react'

import { GroupConnectorLines } from '~/client/src/shared/components/GroupHeader'
import { LocationReference } from '~/client/src/shared/components/ObjectReference/LocationReference'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { cn } from '~/client/src/shared/utils/cn'

import { useActiveLiveMapSlot } from './hooks/useActiveLiveMapSlot'
import { MapViewsSidebarRowMenu } from './MapViewsSidebarRowMenu'
import { MapViewsSidebarThumbnail } from './MapViewsSidebarThumbnail'
import { PublishToAppKey, PublishToChipRow } from './PublishToChipRow'

// Rendered as div[role=option] (not <button>) so admin mode can embed nested
// interactive controls — HTML5 forbids interactive children inside a button.
// Enter/Space activation wired manually to preserve the a11y contract.

export type MapViewCapability = 'georeferenced' | 'whiteboard'

/** Root-first LBS level. Empty ancestry → project-scoped. */
export interface ILbsLevel {
  id: string
  label: string
  iconName: MapViewLocationIcon
  color?: string
}

/** Per-app published-to flags. Mirrors appState's `*MapIdsList` membership. */
export interface IMapViewPublishedTo {
  home: boolean
  workflows: boolean
  deliveries: boolean
  schedule: boolean
}

/** Render-time fields only — sort/group inputs live separately on IMapViewRowSortInputs. */
export interface IMapViewRow {
  id: string
  name: string
  filledImage?: string
  scopeLabel: string
  scopeIconName: MapViewLocationIcon
  /** Undefined for bare-project scope → label inherits foreground. */
  scopeColor?: string
  capability: MapViewCapability
  /** Undefined hides the chip row entirely (read-only consumers). */
  publishedTo?: IMapViewPublishedTo
  /** False hides the Delete entry — UI-side guard for the last-globe-in-project case. */
  canDelete?: boolean
}

/** Separated from `IMapViewRow` so a renderer can't reach for `createdAt` without going through the transform. */
export interface IMapViewRowSortInputs {
  createdAt: number
  updatedAt: number
  /** Empty → project-scoped (renders under the implicit "Project" root band). */
  lbsAncestry: readonly ILbsLevel[]
}

export type IMapViewItem = IMapViewRow & IMapViewRowSortInputs

interface IProps {
  item: IMapViewRow
  isSelected: boolean
  onSelect: (id: string) => void
  /** Singleton — only ONE card's preview ever mounts (see `useActiveLiveMapSlot`). */
  renderLivePreview?: (item: IMapViewRow) => React.ReactNode
  /** Menu needs onRename+onDelete; drag handle needs onReorder. */
  canEdit?: boolean
  onRename?: (id: string) => void
  onDelete?: (id: string) => void
  onCopy?: (id: string) => void
  onSetLocation?: (id: string, anchor: HTMLElement) => void
  /**
   * `position` is load-bearing: bottom-half of the LAST row is the only way to
   * drop something at the end of the list.
   */
  onReorder?: (
    fromId: string,
    toId: string,
    position: 'before' | 'after',
  ) => void
  availableApps?: PublishToAppKey[]
  onOpenPublishTo?: (id: string, anchor: HTMLElement) => void
  /** Extra left padding added to `tw:pl-6` so content indents but full-width borders/hover/selection stretch edge-to-edge. */
  contentIndentPx?: number
  /** Kept on the row (not the virtualizer wrapper) so connector lines paint above the row's background. */
  lbsLineLevel?: number
}

// Custom MIME so we only accept drops from our own list.
const REORDER_MIME = 'application/x-map-views-sidebar-reorder'

// Safari/WebKit hands back a DOMStringList without `.includes`, so a bare
// `types.includes(MIME)` throws on the first dragover — kills reorder on touch
// browsers that drag-drop-touch enables. `Array.from` normalizes both shapes.
const dragTypesInclude = (
  types: DataTransfer['types'],
  mime: string,
): boolean => Array.from(types).includes(mime)

const ITEM_BASE_PL_PX = 24 // matches `tw:pl-6`

// Band chevron centre = 16px band pl + depth * 24 + 8 (half the 16px chevron); rows share
// the band's x origin, so their lines continue under each ancestor chevron.
export const LBS_CONNECTOR_OFFSET_PX = 24

export const MapViewsSidebarListItem: React.FC<IProps> = ({
  item,
  isSelected,
  onSelect,
  renderLivePreview,
  canEdit = false,
  onRename,
  onDelete,
  onCopy,
  onSetLocation,
  onReorder,
  availableApps,
  onOpenPublishTo,
  contentIndentPx,
  lbsLineLevel,
}) => {
  const isGeoreferenced = item.capability === 'georeferenced'
  const capabilityLabel = isGeoreferenced
    ? Localization.translator.georeferenced
    : Localization.translator.whiteboard

  const liveEnabled = !!renderLivePreview
  const { isActive, scheduleActivate, cancelActivate } = useActiveLiveMapSlot(
    item.id,
    { enabled: liveEnabled },
  )

  const showRowMenu = canEdit && !!onRename && !!onDelete
  const showDragHandle = canEdit && !!onReorder
  const isLocationClickable = canEdit && !!onSetLocation
  // `draggable` lives ONLY on the handle, not the row — the drag-drop-touch
  // polyfill latches onto any draggable element it sees and would kill list
  // scrolling on touch. Drag image is reset to the row in handleDragStart.
  const rowRef = React.useRef<HTMLDivElement | null>(null)
  const [isDragSource, setIsDragSource] = React.useState(false)
  // Modeled as a discriminator (not a bare bool) so the LAST row's bottom half
  // is a reachable drop slot — required to drop something at the end of the list.
  const [dropSide, setDropSide] = React.useState<'before' | 'after' | null>(
    null,
  )

  const handleSelect = (): void => onSelect(item.id)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect(item.id)
    }
  }

  const handleDragStart = (e: React.DragEvent<HTMLSpanElement>): void => {
    e.stopPropagation()
    // Carry the stable card id, NOT visible-list index — filtered lists break index mapping.
    e.dataTransfer.setData(REORDER_MIME, item.id)
    e.dataTransfer.effectAllowed = 'move'
    if (rowRef.current) {
      e.dataTransfer.setDragImage(rowRef.current, 0, 0)
    }
    setIsDragSource(true)
  }
  const handleDragEnd = (e: React.DragEvent<HTMLSpanElement>): void => {
    e.stopPropagation()
    setIsDragSource(false)
    setDropSide(null)
  }
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    if (!showDragHandle) return
    if (!dragTypesInclude(e.dataTransfer.types, REORDER_MIME)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const rect = e.currentTarget.getBoundingClientRect()
    const nextSide: 'before' | 'after' =
      e.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
    if (dropSide !== nextSide) setDropSide(nextSide)
  }
  const handleDragLeave = (): void => {
    if (dropSide) setDropSide(null)
  }
  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    if (!onReorder) return
    const fromId = e.dataTransfer.getData(REORDER_MIME)
    if (!fromId) return
    e.preventDefault()
    // Fresh midpoint check at drop time — touch dragover fires sparingly, so dropSide state may lag.
    const rect = e.currentTarget.getBoundingClientRect()
    const position: 'before' | 'after' =
      dropSide ?? (e.clientY < rect.top + rect.height / 2 ? 'before' : 'after')
    setDropSide(null)
    if (fromId !== item.id) onReorder(fromId, item.id, position)
  }

  return (
    <div
      ref={rowRef}
      role="option"
      tabIndex={0}
      data-slot="map-views-sidebar-list-item"
      data-drag-source={isDragSource || undefined}
      data-drop-side={dropSide || undefined}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      onMouseEnter={liveEnabled ? scheduleActivate : undefined}
      onMouseLeave={liveEnabled ? cancelActivate : undefined}
      onFocus={liveEnabled ? scheduleActivate : undefined}
      onBlur={liveEnabled ? cancelActivate : undefined}
      onDragOver={showDragHandle ? handleDragOver : undefined}
      onDragLeave={showDragHandle ? handleDragLeave : undefined}
      onDrop={showDragHandle ? handleDrop : undefined}
      aria-selected={isSelected}
      aria-label={Localization.translator.selectMapViewX(item.name)}
      style={
        contentIndentPx
          ? { paddingLeft: ITEM_BASE_PL_PX + contentIndentPx }
          : undefined
      }
      className={cn({
        // `pl-6` leaves a gutter for the absolute drag handle.
        // `/50` so nested ghost Buttons (full bg-muted on hover) stay distinct.
        'tw:group/list-item tw:relative tw:flex tw:flex-col tw:gap-3 tw:pl-6 tw:pr-4 tw:py-4 tw:w-full tw:border-0 tw:border-b tw:border-solid tw:border-border tw:bg-transparent tw:text-left tw:cursor-pointer tw:select-none tw:outline-none tw:transition-colors tw:duration-150 tw:hover:bg-muted/50 tw:focus-visible:bg-muted/50': true,
        'tw:bg-primary/8 tw:hover:bg-primary/8 tw:focus-visible:bg-primary/8':
          isSelected,
        'tw:opacity-50': isDragSource,
        'tw:before:absolute tw:before:inset-x-0 tw:before:top-0 tw:before:h-0.5 tw:before:bg-primary tw:before:content-[""]':
          dropSide === 'before',
        'tw:after:absolute tw:after:inset-x-0 tw:after:bottom-0 tw:after:h-0.5 tw:after:bg-primary tw:after:content-[""]':
          dropSide === 'after',
      })}
    >
      {lbsLineLevel ? (
        <GroupConnectorLines
          depth={lbsLineLevel}
          leftOffsetPx={LBS_CONNECTOR_OFFSET_PX}
        />
      ) : null}
      {showDragHandle && (
        <DragHandle
          ariaLabel={`${Localization.translator.reorder} ${item.name}`}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        />
      )}
      {/* `items-start` pins the thumbnail to the top so a wrapped title grows downward
          instead of re-centering the thumbnail. */}
      <div className="tw:flex tw:gap-2 tw:items-start tw:w-full tw:min-w-0">
        <MapViewsSidebarThumbnail
          src={item.filledImage}
          alt={item.name}
          isLive={isActive}
          renderLivePreview={
            renderLivePreview ? () => renderLivePreview(item) : undefined
          }
        />
        <div className="tw:flex tw:flex-col tw:gap-2 tw:items-start tw:flex-1 tw:min-w-0">
          {/* pl-2.5 matches Button's px-2.5 so titles align with row icons below.
              pr-2 (8px) makes the kebab's right gap (16 + 8 = 24px) mirror the map's 24px left gap. */}
          <div className="tw:flex tw:gap-1 tw:items-start tw:w-full tw:pl-2.5 tw:pr-2">
            <span
              className="tw:flex-1 tw:min-w-0 tw:text-title-md tw:text-foreground tw:line-clamp-2"
              title={item.name}
            >
              {item.name}
            </span>
            {/* -my-1 keeps the 32px row-menu trigger from inflating the title row's 24px line-height. */}
            {(isSelected || showRowMenu) && (
              <div className="tw:flex tw:items-center tw:gap-2 tw:shrink-0 tw:-my-1">
                {isSelected && (
                  <CheckIcon
                    className="tw:size-6 tw:text-primary"
                    aria-hidden="true"
                  />
                )}
                {showRowMenu && (
                  <MapViewsSidebarRowMenu
                    itemId={item.id}
                    itemName={item.name}
                    onRename={onRename}
                    onDelete={onDelete}
                    onCopy={onCopy}
                    canDelete={item.canDelete !== false}
                  />
                )}
              </div>
            )}
          </div>
          <div className="tw:flex tw:flex-col tw:items-start tw:w-full">
            {isLocationClickable ? (
              // Span stops pointerdown (fires before click → would reach the row's pointer-select).
              // inline-flex keeps the wrapper sized to the 18px inner button, not the 23px line-height.
              <span
                className="tw:inline-flex tw:items-center"
                onPointerDown={e => e.stopPropagation()}
              >
                <LocationReference
                  iconName={item.scopeIconName}
                  color={item.scopeColor}
                  variant="link"
                  size="xs"
                  onActivate={anchor => onSetLocation?.(item.id, anchor)}
                >
                  {item.scopeLabel}
                </LocationReference>
              </span>
            ) : (
              <LocationReference
                iconName={item.scopeIconName}
                color={item.scopeColor}
                variant="plain"
                size="xs"
                className="tw:h-8 tw:px-2.5 tw:gap-1.5"
              >
                {item.scopeLabel}
              </LocationReference>
            )}
            {/* h-8 px-2.5 gap-1.5 mirrors the sibling ghost Buttons so all three rows share one grid. */}
            <span className="tw:inline-flex tw:items-center tw:gap-1.5 tw:h-8 tw:px-2.5 tw:text-foreground tw:max-w-full">
              {isGeoreferenced ? (
                <GlobeIcon
                  className="tw:size-4 tw:shrink-0 tw:text-muted-foreground"
                  aria-hidden="true"
                />
              ) : (
                <ImageIcon
                  className="tw:size-4 tw:shrink-0 tw:text-muted-foreground"
                  aria-hidden="true"
                />
              )}
              <span className="tw:text-label-md">{capabilityLabel}</span>
            </span>
            {item.publishedTo && availableApps && availableApps.length > 0 && (
              <PublishToChipRow
                publishedTo={item.publishedTo}
                availableApps={availableApps}
                onClick={
                  onOpenPublishTo
                    ? anchor => onOpenPublishTo(item.id, anchor)
                    : undefined
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Always visible under `pointer-coarse` (touch / Surface Pro) since hover
// never fires reliably there; hover-reveal under `pointer-fine`.
interface IDragHandleProps {
  ariaLabel: string
  onDragStart: (e: React.DragEvent<HTMLSpanElement>) => void
  onDragEnd: (e: React.DragEvent<HTMLSpanElement>) => void
}
const DragHandle: React.FC<IDragHandleProps> = ({
  ariaLabel,
  onDragStart,
  onDragEnd,
}) => (
  <span
    aria-label={ariaLabel}
    data-slot="map-views-sidebar-drag-handle"
    draggable={true}
    onClick={e => e.stopPropagation()}
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    className="tw:absolute tw:left-0 tw:top-1/2 tw:-translate-y-1/2 tw:flex tw:items-center tw:justify-center tw:w-6 tw:h-12 tw:p-2 tw:text-muted-foreground tw:cursor-grab tw:active:cursor-grabbing tw:opacity-0 tw:transition-opacity tw:duration-150 tw:group-hover/list-item:opacity-100 tw:group-focus-within/list-item:opacity-100 tw:pointer-coarse:opacity-100"
  >
    <span className="tw:flex tw:items-center tw:justify-center tw:size-6 tw:rounded-md tw:transition-colors tw:duration-150 tw:hover:bg-muted">
      <GripVerticalIcon className="tw:size-3" aria-hidden="true" />
    </span>
  </span>
)
